import { isAxiosError } from 'axios'

import { api } from '@/api/client'

/**
 * Regras de ordem por alerta (spec 2026-09-30 §6). Quem decide é o servidor
 * (`regras_de_ordem/rotas_service.py`): criar só a partir de uma ordem concluída
 * que baixou o disco, pelo solicitante; pausar/retomar/revogar pelo dono ou pelo
 * Gestor do tenant. Esta camada só leva o pedido e traduz a recusa em frase.
 */

export type EstadoDaRegra = 'ativa' | 'pausada' | 'revogada'

/** Uma linha do `GET …/regras-de-ordem` (`rotas_service.listar`). */
export interface RegraDeOrdem {
  id: number
  dono_user_id?: number
  dono_nome: string
  tenant_id?: number
  host_padrao: string
  gatilho?: string
  montagem?: string
  limiar_pct?: number
  verbos?: string[]
  modo?: string
  teto_semana?: number
  valida_ate: string
  estado: EstadoDaRegra
  motivo_estado?: string | null
  origem_plan_id?: string | null
  criada_em?: string
  limpas: number
  nao_limpas: number
  pendentes: number
  /** Se QUEM OLHA pode pausar/retomar/revogar; quem decide de novo é o servidor. */
  pode_administrar: boolean
}

export interface CriarRegraBody {
  origem_plan_id: string
  host_padrao: string
  teto_semana: number
  valida_dias: number
}

export type AcaoDaRegra = 'pausar' | 'retomar' | 'revogar'

const base = (tenantId: number) => `/v1/tenants/${tenantId}/regras-de-ordem`

export function criarRegra(tenantId: number, body: CriarRegraBody): Promise<RegraDeOrdem> {
  return api.post<RegraDeOrdem>(base(tenantId), body).then((r) => r.data)
}

export function listarRegras(tenantId: number): Promise<RegraDeOrdem[]> {
  return api
    .get<{ items: RegraDeOrdem[] }>(base(tenantId))
    .then((r) => r.data.items ?? [])
}

export function mudarEstadoRegra(
  tenantId: number,
  id: number,
  acao: AcaoDaRegra,
): Promise<{ id: number; estado: EstadoDaRegra; motivo_estado: string | null }> {
  return api.post(`${base(tenantId)}/${id}/${acao}`).then((r) => r.data)
}

/** O mesmo teste de `rotas_service.padrao_cobre_o_host`: o próprio host, ou um
 * prefixo dele terminado em `*` (e só um `*`, no fim). */
export function padraoCobreOHost(hostPadrao: string, host: string): boolean {
  if (hostPadrao === host) return true
  return hostPadrao.endsWith('*') && hostPadrao.length > 1
    && !hostPadrao.slice(0, -1).includes('*') && host.startsWith(hostPadrao.slice(0, -1))
}

// ── Recusas ────────────────────────────────────────────────────────────────

/** Cada código que `RegraRecusada` e os portões de `gates.check_all` podem devolver,
 * em uma frase. O código cru nunca chega à tela. */
const FRASES: Record<string, string> = {
  ordem_desconhecida: 'Não encontrei esta ordem neste ambiente.',
  nao_e_o_solicitante: 'Só quem pediu a ordem pode criar uma regra a partir dela.',
  limite_fora: 'O teto por semana vai de 1 a 14, e a validade, de 1 a 90 dias.',
  so_em_producao: 'Regra de ordem só vale em ambiente de produção.',
  ordem_nao_concluida: 'A regra só nasce de uma ordem que terminou concluída, e esta não terminou assim.',
  ordem_sem_baixa: 'A remedição desta ordem não mostrou o disco baixando. A regra só nasce de uma limpeza que funcionou.',
  host_do_alerta_desconhecido: 'Esta ordem não guardou o host do alerta de onde nasceu, então a regra não teria com o que comparar os próximos alertas.',
  padrao_fora_do_host: 'O padrão precisa ser o próprio host ou um começo dele terminado em *.',
  verbo_desconhecido: 'A limpeza usada nesta ordem não está mais no catálogo.',
  verbo_fora_do_degrau_3: 'A regra só vale para limpezas que passam pela aprovação do time, e esta não passa.',
  portao_indisponivel: 'Não consegui conferir agora as suas permissões. Tente de novo em instantes.',
  limiar_desconhecido: 'Esta ordem não guardou o limiar do alerta, então a regra não teria o que copiar.',
  regra_ja_existe: 'Já existe uma regra criada a partir desta ordem. Ela está na lista de regras de ordem.',
  // Portões do chat (`gates.check_all`), conferidos na criação e no retomar.
  cota_excedida: 'A cota de ordens deste ambiente está esgotada agora. Tente de novo mais tarde.',
  degrau_invalido: 'Esta limpeza não pode ser pedida por regra neste ambiente.',
  exige_gmud: 'Esta limpeza exige uma GMUD neste ambiente.',
  gmud_ausente: 'Esta limpeza exige uma GMUD neste ambiente, e não há uma aberta.',
  gmud_invalida: 'A GMUD desta limpeza não vale mais.',
  instancia_protegida: 'Esta máquina está protegida contra ordens no host.',
  papel_insuficiente: 'O seu papel não permite pedir esta limpeza neste ambiente.',
  permissao_ausente: 'Você não tem a permissão de pedir esta limpeza neste ambiente.',
  verbo_inativo: 'Esta limpeza está desligada.',
  verbo_inativo_em_producao: 'Esta limpeza está desligada em produção.',
  // Mudança de estado.
  regra_desconhecida: 'Esta regra não existe mais neste ambiente.',
  transicao_invalida: 'A regra mudou de estado enquanto você olhava. Atualize a lista.',
  regra_vencida: 'A regra venceu e não pode ser retomada. Crie outra a partir de uma ordem nova.',
  acao_desconhecida: 'Ação desconhecida para a regra.',
  // A dependência `user_ctx` da rota recusa quem não está no tenant do caminho.
  user_not_in_tenant: 'Você não tem acesso a este ambiente.',
}

const SEM_PERMISSAO: Record<'listar' | 'mudar' | 'criar', string> = {
  listar: 'Você não tem permissão para ver as regras de ordem deste ambiente.',
  mudar: 'Só o dono da regra ou o Gestor do ambiente pode mudar o estado dela.',
  criar: 'Você não tem permissão para criar regras neste ambiente.',
}

/** No `mudar`, os portões são conferidos com o DONO (retomar refaz a conferência da
 * criação com ele, §6.3) — quem clicou pode ser o Gestor. A frase fala do dono. */
const FRASES_DO_DONO: Record<string, string> = {
  permissao_ausente: 'O dono da regra não pode mais pedir esta limpeza neste ambiente.',
  papel_insuficiente: 'O papel do dono da regra não permite mais pedir esta limpeza neste ambiente.',
  portao_indisponivel: 'Não consegui conferir agora as permissões do dono da regra. Tente de novo em instantes.',
  dono_sem_permissao: 'O dono da regra perdeu a permissão de diagnosticar neste ambiente.',
  dono_sem_acesso_a_conversa: 'O dono da regra perdeu o acesso à conversa dos alertas deste ambiente.',
}

const GENERICA = 'Não foi possível concluir. Tente de novo; se persistir, é caso de olhar o log da API.'

/** O código da recusa (`detail` de texto) de um erro HTTP, ou null. */
export function codigoDaRecusa(erro: unknown): string | null {
  if (!isAxiosError(erro)) return null
  const d = (erro.response?.data as { detail?: unknown } | undefined)?.detail
  return typeof d === 'string' ? d : null
}

export function fraseDaRecusa(erro: unknown, onde: 'listar' | 'mudar' | 'criar'): string {
  const codigo = codigoDaRecusa(erro)
  if (codigo === 'sem_permissao') return SEM_PERMISSAO[onde]
  if (onde === 'mudar' && codigo && FRASES_DO_DONO[codigo]) return FRASES_DO_DONO[codigo]
  return (codigo && FRASES[codigo]) || GENERICA
}
